// i18n setup — namespace-per-feature, locale source-of-truth chain per
// operator instructions:
//   1. user.preferred_locale (server-side, on `me` payload)  [highest]
//   2. localStorage 'nexus.locale'                            (website choice + dashboard prior session)
//   3. navigator.language                                     (browser default)
//   4. geo-IP fallback via Cloudflare CF-IPCountry header     (server-side check; here we honour the
//                                                              cookie set by the api-gateway: 'cf-country')
//   5. hardcoded fallback: 'tr'
//
// Why this order: a user who explicitly switches language on the marketing
// site should NOT have it overridden by their browser default the next time
// they hit the dashboard. And a logged-in user's profile preference beats
// everything.
//
// The geo-IP fallback only kicks in when nothing else is set — Cloudflare's
// CF-IPCountry header is mirrored to a `cf-country` cookie by api-gateway
// so we can read it client-side too.
//
// Constants:
//   - Supported locales: 'tr' (default) and 'en'
//   - TR-leaning countries: TR + KKTC + AZ + UZ + KZ + TM → default TR
//   - everything else → default EN

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

const TR_LEANING_COUNTRIES = new Set([
  "TR", // Türkiye
  "CY", // Cyprus (KKTC overlap)
  "AZ", // Azerbaijan
  "UZ", // Uzbekistan
  "KZ", // Kazakhstan
  "TM", // Turkmenistan
  "KG", // Kyrgyzstan
]);

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

// Custom detector chain — overrides the default ordering with our priority.
//
// i18next-browser-languagedetector's CustomDetector interface uses
// `lookup()` (NOT `detect()`). Easy to misread because the parent
// LanguageDetector module exposes `detect()` as its public method —
// but custom sub-detectors registered via `addDetector` must implement
// `lookup`. (Caught in prod 2026-05-22 — the runtime threw
// "this.detectors[o].lookup is not a function" on init.)
class NexusLocaleDetector {
  static type = "languageDetector" as const;
  name = "nexus";
  lookup(): string | undefined {
    // 0. explicit ?lang= handover from the marketing site. localStorage does
    //    NOT cross origins (nexusdijital.com vs app.nexusdijital.com), so the
    //    landing's language can only arrive via the URL. Persist immediately
    //    so it sticks for later visits.
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q === "tr" || q === "en") {
      localStorage.setItem("nexus.locale", q);
      return q;
    }
    // 1. user preference (set by AuthProvider after /me responds; written to localStorage)
    const profile = localStorage.getItem("nexus.user.preferred_locale");
    if (profile === "tr" || profile === "en") return profile;
    // 2. localStorage (previous dashboard session / ?lang handover)
    const ls = localStorage.getItem("nexus.locale");
    if (ls === "tr" || ls === "en") return ls;
    // 3. geo-IP via cf-country cookie when present. Geo goes BEFORE
    //    navigator.language on purpose: the landing picks language by geo,
    //    so a Turkish visitor with an English-UI browser was getting a TR
    //    landing → EN panel mismatch (operator report 2026-07-06). The
    //    cookie is best-effort; the async /cdn-cgi/trace boot check below
    //    covers first visits where no cookie exists.
    const cc = readCookie("cf-country")?.toUpperCase();
    if (cc && TR_LEANING_COUNTRIES.has(cc)) return "tr";
    if (cc) return "en";
    // 4. navigator.language
    const nav = (navigator.language || "").slice(0, 2).toLowerCase();
    if (nav === "tr" || nav === "en") return nav;
    // 5. hardcoded fallback — Türkiye is the primary market
    return "tr";
  }
  cacheUserLanguage(lng: string) {
    localStorage.setItem("nexus.locale", lng);
    document.documentElement.lang = lng;
  }
  init() {} // required by the interface
}

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "tr",
    supportedLngs: ["tr", "en"],
    // Just 'common' for now — all inline JSX strings are still hardcoded
    // (waiting on the TR copy review). Adding more namespaces here would
    // make i18next preload empty 404s. When we split per-page bundles,
    // create public/locales/<lng>/<ns>.json first and only then add to
    // this list.
    ns: ["common"],
    defaultNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    detection: {
      // We provide our own detector; this disables the built-in chain.
      order: ["nexus"],
    },
    react: {
      useSuspense: true,
    },
    interpolation: {
      escapeValue: false,
    },
  });

// Register our custom detector. Done after init() because the constructor pulls
// the detector list at init time but we need it on the services bag first.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(i18n as any).services.languageDetector.addDetector(new NexusLocaleDetector());
// Re-trigger detection so the custom detector actually runs.
void i18n.changeLanguage();

// First-visit geo check — app.nexusdijital.com sits behind Cloudflare, whose
// /cdn-cgi/trace endpoint returns `loc=<ISO country>` same-origin. Only runs
// when the user has NO persisted choice (no ?lang, no profile, no prior
// session), i.e. the language came from the navigator/fallback guess. Aligns
// the panel with the geo-based language the marketing site showed. Non-fatal:
// on any error we keep the current guess.
void (async () => {
  try {
    const hasChoice =
      new URLSearchParams(window.location.search).get("lang") ||
      localStorage.getItem("nexus.user.preferred_locale") ||
      localStorage.getItem("nexus.locale");
    if (hasChoice) return;
    const res = await fetch("/cdn-cgi/trace", { cache: "no-store" });
    if (!res.ok) return;
    const text = await res.text();
    const loc = /(?:^|\n)loc=([A-Z]{2})/.exec(text)?.[1];
    if (!loc) return;
    const want = TR_LEANING_COUNTRIES.has(loc) ? "tr" : "en";
    if (i18n.resolvedLanguage !== want) {
      await i18n.changeLanguage(want); // cacheUserLanguage persists it
    } else {
      localStorage.setItem("nexus.locale", want); // lock in the correct guess
    }
  } catch {
    /* geo is best-effort */
  }
})();

export default i18n;
